'use client';
import React, { useState } from 'react';

type Mode = 'encode' | 'decode';
type InputType = 'text' | 'file';

export default function Base64Tool() {
  const [mode, setMode] = useState<Mode>('encode');
  const [inputType, setInputType] = useState<InputType>('text');
  const [text, setText] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [fileName, setFileName] = useState('');

  const handleProcess = () => {
    setError('');
    setResult('');
    if (!text.trim()) return;
    try {
      if (mode === 'encode') {
        const encoded = btoa(unescape(encodeURIComponent(text)));
        setResult(encoded);
      } else {
        const decoded = decodeURIComponent(escape(atob(text.trim())));
        setResult(decoded);
      }
    } catch {
      setError(mode === 'decode' ? 'Invalid Base64 string. Please check your input.' : 'Encoding failed. Please try again.');
    }
  };

  const handleFileEncode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      setResult(base64);
      setText('');
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([result], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `base64-${mode}d.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSwap = () => {
    if (result) {
      setText(result);
      setResult('');
      setMode((m) => m === 'encode' ? 'decode' : 'encode');
    }
  };

  return (
    <div className="space-y-5">
      {/* Mode selector */}
      <div className="flex gap-2">
        {(['encode', 'decode'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setResult(''); setError(''); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${mode === m ? 'bg-brand-blue text-white' : 'bg-surface text-text-secondary hover:bg-gray-100'}`}
          >
            {m === 'encode' ? '🔒 Encode' : '🔓 Decode'}
          </button>
        ))}
      </div>

      {/* Input type (encode only) */}
      {mode === 'encode' && (
        <div className="flex gap-2">
          {(['text', 'file'] as InputType[]).map((t) => (
            <button
              key={t}
              onClick={() => { setInputType(t); setResult(''); setError(''); setText(''); setFileName(''); }}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${inputType === t ? 'bg-blue-100 text-brand-blue' : 'bg-surface text-text-secondary hover:bg-gray-100'}`}
            >
              {t === 'text' ? 'Text Input' : 'File Input'}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      {(mode === 'decode' || inputType === 'text') ? (
        <div>
          <label className="font-semibold text-text-primary text-sm mb-2 block">
            {mode === 'encode' ? 'Text to Encode' : 'Base64 String to Decode'}
          </label>
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); setResult(''); setError(''); }}
            placeholder={mode === 'encode' ? 'Enter text to encode to Base64…' : 'Paste Base64 string to decode…'}
            className="w-full h-32 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-text-primary font-mono resize-none focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
          />
        </div>
      ) : (
        <div>
          <label className="font-semibold text-text-primary text-sm mb-2 block">File to Encode</label>
          <label className="upload-zone rounded-2xl p-6 text-center cursor-pointer block">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <p className="text-sm font-medium text-text-primary">{fileName || 'Click to choose a file'}</p>
            <p className="text-xs text-text-muted mt-1">Any file type supported</p>
            <input type="file" className="hidden" onChange={handleFileEncode} />
          </label>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-700">{error}</div>
      )}

      {(mode === 'decode' || inputType === 'text') && (
        <button
          onClick={handleProcess}
          disabled={!text.trim()}
          className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {mode === 'encode' ? 'Encode to Base64' : 'Decode from Base64'}
        </button>
      )}

      {result && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="font-semibold text-text-primary text-sm">Result</label>
            <div className="flex gap-2">
              {result && text && (
                <button onClick={handleSwap} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-text-secondary hover:bg-gray-200 transition-colors font-medium">⇄ Swap</button>
              )}
              <button onClick={handleCopy} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-brand-blue hover:bg-blue-100 transition-colors font-medium">
                {copied ? '✓ Copied' : 'Copy'}
              </button>
              <button onClick={handleDownload} className="text-xs px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors font-medium">Download</button>
            </div>
          </div>
          <textarea
            readOnly
            value={result}
            className="w-full h-32 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-text-secondary font-mono resize-none focus:outline-none bg-gray-50"
          />
          <p className="text-xs text-text-muted mt-1">{result.length} characters</p>
        </div>
      )}
    </div>
  );
}
